import { useId, useState } from 'react';
import { Result, setFirmwareAsDesired } from '../../api/api';
import { IDevice } from '../../api/types';
import { Button } from '../Common/Button';
import { DeviceCommandStatus } from './DeviceCommandStatus';

interface IProps {
  device: IDevice;
  desiredFirmwareVersion: number;
  onSubmitted?: () => void;
}

export const SendSetFirmware = ({
  device,
  desiredFirmwareVersion,
  onSubmitted,
}: IProps) => {
  const [firmware, setFirmware] = useState<string>(
    desiredFirmwareVersion.toString(),
  );
  const [firmwareAsDesiredResult, setFirmwareAsDesiredResult] =
    useState<Result<string>>({ status: '' });
  const inputId = useId();
  const validationId = `${inputId}-validation`;

  const parsedFirmware = Number(firmware);
  const isValidFirmware =
    Number.isInteger(parsedFirmware) && parsedFirmware > 0;

  const setFirmwareAsDesiredFunc = async () => {
    if (
      !isValidFirmware ||
      firmwareAsDesiredResult.status === 'Loading'
    ) {
      return;
    }

    setFirmwareAsDesiredResult({ status: 'Loading' });
    const result = await setFirmwareAsDesired(
      device.productCode,
      [device.serialNumber],
      parsedFirmware,
      device.variant,
    );
    setFirmwareAsDesiredResult(result);
    if (result.status === 'OK') {
      onSubmitted?.();
    }
  };

  return (
    <div className="bg-gray-50 w-50 rounded overflow-hidden m-4">
      <div className="px-6 py-4">
        <div className="font-bold text-xl mb-2">
          <div className="mb-5">Set desired firmware</div>
          <div>
            <div className="flex-row mt-2">
              <label htmlFor={inputId} className="mr-2">
                Desired firmware version
              </label>
              <input
                id={inputId}
                type="number"
                min={1}
                aria-invalid={!isValidFirmware}
                aria-describedby={
                  !isValidFirmware ? validationId : undefined
                }
                disabled={
                  firmwareAsDesiredResult.status === 'Loading'
                }
                className="bg-purple-white rounded border-2 border-grey p-3 text-sm"
                value={firmware}
                onChange={(event) =>
                  setFirmware(event.currentTarget.value)
                }
              />
              <Button
                onClick={setFirmwareAsDesiredFunc}
                text={'Send'}
                disabled={[
                  {
                    disabled:
                      firmwareAsDesiredResult.status === 'Loading',
                    reason: 'A request is already being submitted.',
                  },
                  {
                    disabled: !isValidFirmware,
                    reason:
                      'Enter a positive whole-number firmware version.',
                  },
                ]}
              />
            </div>
            {!isValidFirmware && (
              <div
                id={validationId}
                className="text-xs text-red-600 mt-2"
                role="alert"
              >
                Enter a positive whole-number firmware version.
              </div>
            )}
            <DeviceCommandStatus
              result={firmwareAsDesiredResult}
              kind="desired"
            />
          </div>
          <p className="mt-3 text-xs font-thin text-gray-600">
            This command will tell the device to upgrade <br /> to the
            specific firmware.
          </p>
        </div>
      </div>
    </div>
  );
};
